// @flow

import * as React from 'react';

import { toUnicode } from 'punycode.js';

import { selectSmdFileInfos } from '@gandi/cart';
import { useTranslator, DateTime } from '@gandi/translate';
import { Badge, Base, Svg, Button, Text, TagList } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import type { SmdFileInfo } from '../types';

import styles from './FileInfo.scss';

type Props = {
  fqdn: string,
  setRemovedByUser: boolean => void,
};

export default function FileInfo({
  fqdn,
  setRemovedByUser,
}: Props): null | React.Node {
  const { __ } = useTranslator();

  // FIXME: move SmdFileInfo type to cart
  // $FlowFixMe[incompatible-type]
  const smdFileInfos: void | SmdFileInfo = useAppSelector(state =>
    selectSmdFileInfos(state, { fqdn }),
  );

  if (!smdFileInfos) {
    return null;
  }

  if (!smdFileInfos.isValid) {
    return <Text color="danger">{__('The file doesn’t seem correct.')}</Text>;
  }

  return (
    <>
      <Base weight="semibold" marginBottom="xsmall">
        {__('SMD File')}
      </Base>
      <Base flex flexAlignItemsCenter marginBottom="small">
        <Svg theme="success" name="checkmark" className={styles.icon} />
        <Base tagName="span" marginRight="small" weight="semibold">
          {smdFileInfos.name}
        </Base>
        <Button
          kind="link"
          type="button"
          onClick={() => setRemovedByUser(true)}
        >
          {__('Remove')}
        </Button>
      </Base>
      <h2>{smdFileInfos.marks}</h2>
      {smdFileInfos.notAfter && (
        <Base marginBottom="small">
          <Base weight="semibold" marginBottom="xsmall">
            {__('Valid until')}
          </Base>
          <Badge>
            <DateTime format="LL">{smdFileInfos.notAfter}</DateTime>
          </Badge>
        </Base>
      )}
      {smdFileInfos.smdID && (
        <Base marginBottom="small">
          <h4>{__('SMD file ID')}</h4>
          {smdFileInfos.smdID}
        </Base>
      )}

      {smdFileInfos.labels && smdFileInfos.labels.length > 0 && (
        <>
          <h4>{__('SMD file labels')}</h4>
          <TagList
            id="labels"
            // https://flow.org/en/docs/lang/refinements/#toc-refinement-invalidations
            // $FlowFixMe presence of `labels` is tested above
            tags={smdFileInfos.labels.map(label => ({
              label: toUnicode(label),
            }))}
          />
        </>
      )}
    </>
  );
}
